import { RefreshCw, Wifi } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { usePortAvailability } from '../../hooks'
import { PortStatusIndicator } from './PortStatusIndicator'

const RADIX_DECIMAL = 10

type PortAvailabilityResult = ReturnType<typeof usePortAvailability>

interface PortFieldProps {
  label: string
  value: number
  onChange: (value: number) => void
  ports: PortAvailabilityResult['ports']
}

function PortField({ label, value, onChange, ports }: PortFieldProps): React.ReactElement {
  const status = ports.find((portInfo) => portInfo.port === value)
  return (
    <div>
      <label className="mb-1 block text-xs text-stone-500 dark:text-stone-400">{label}</label>
      <input
        type="number"
        value={value}
        onChange={(ev) => {
          const parsed = Number.parseInt(ev.target.value, RADIX_DECIMAL)
          onChange(Number.isNaN(parsed) ? 0 : parsed)
        }}
        className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-stone-900 dark:border-stone-600 dark:bg-stone-700 dark:text-stone-100"
      />
      {status ? <PortStatusIndicator status={status} /> : null}
    </div>
  )
}

interface CheckPortsButtonProps {
  checking: boolean
  onCheck: () => void
}

function CheckPortsButton({ checking, onCheck }: CheckPortsButtonProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <button
      type="button"
      onClick={onCheck}
      disabled={checking}
      className="flex items-center gap-1 text-sm text-[#5c6e94] hover:underline dark:text-[#8a9bba]"
    >
      {checking ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Wifi className="h-3 w-3" />}
      {t('velociraptorSettings.install.checkAvailability')}
    </button>
  )
}

interface PortConfigSectionProps {
  apiPort: number
  guiPort: number
  frontendPort: number
  setApiPort: (value: number) => void
  setGuiPort: (value: number) => void
  setFrontendPort: (value: number) => void
  portAvailability: PortAvailabilityResult
}

export function PortConfigSection({
  apiPort,
  guiPort,
  frontendPort,
  setApiPort,
  setGuiPort,
  setFrontendPort,
  portAvailability,
}: PortConfigSectionProps): React.ReactElement {
  const { t } = useTranslation('admin')
  const handleCheck = (): void => {
    void portAvailability.checkCustom([apiPort, guiPort, frontendPort])
  }
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <label className="block text-sm font-medium text-stone-700 dark:text-stone-300">
          {t('velociraptorSettings.install.portConfiguration')}
        </label>
        <CheckPortsButton checking={portAvailability.checking} onCheck={handleCheck} />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <PortField
          label={t('velociraptorSettings.install.apiPort')}
          value={apiPort}
          onChange={setApiPort}
          ports={portAvailability.ports}
        />
        <PortField
          label={t('velociraptorSettings.install.guiPort')}
          value={guiPort}
          onChange={setGuiPort}
          ports={portAvailability.ports}
        />
        <PortField
          label={t('velociraptorSettings.install.frontendPort')}
          value={frontendPort}
          onChange={setFrontendPort}
          ports={portAvailability.ports}
        />
      </div>
    </div>
  )
}
